import baseRegistryJson from './school-exercise-registry.json';
import crossDomain100Json from './school-exercise-packs/cross-domain-100.json';
import english25Json from './school-exercise-packs/english-25-v2.json';
import englishVisible50Json from './school-exercise-packs/english-visible-50.json';
import italianVisible50Json from './school-exercise-packs/italian-visible-50.json';
import technologyVisible70Json from './school-exercise-packs/technology-visible-70.json';
import artVisible70Json from './school-exercise-packs/art-visible-70.json';
import measurement25Json from './school-exercise-packs/measurement-25.json';
import infanziaWorld25Json from './school-exercise-packs/infanzia-world-visual-25.json';
import infanziaLanguage25Json from './school-exercise-packs/infanzia-language-visual-25.json';
import infanziaEnglish25Json from './school-exercise-packs/infanzia-english-visual-25.json';
import infanziaBody25Json from './school-exercise-packs/infanzia-body-visual-25.json';
import infanziaArtSensory25Json from './school-exercise-packs/infanzia-art-sensory-25.json';
import informaticsVisible50Json from './school-exercise-packs/informatics-visible-50.json';
import infanziaWorldVisible50Json from './school-exercise-packs/infanzia-world-visible-50.json';
import historyVisible70Json from './school-exercise-packs/history-visible-70.json';
import geographyVisible70Json from './school-exercise-packs/geography-visible-70.json';
import scienceVisible90Json from './school-exercise-packs/science-visible-90.json';
import mathVisibleNumbers30Json from './school-exercise-packs/math-visible-numbers-30.json';
import mathVisibleCalculation30Json from './school-exercise-packs/math-visible-calculation-30.json';
import mathVisibleGeometryMeasure30Json from './school-exercise-packs/math-visible-geometry-measure-30.json';
import musicAudio30Json from './school-exercise-packs/music-audio-30.json';

export type SchoolSpeechLocale = 'it-IT' | 'en-US';
export type SchoolExerciseDeliveryMode = 'text-choice' | 'visual-audio';

export interface SchoolExerciseChoiceRecord {
  visual: string;
  label: string;
}

export interface SchoolExerciseBankItemRecord {
  id: string;
  prompt: string;
  choices: readonly SchoolExerciseChoiceRecord[];
  correctIndex: number;
}

export interface SchoolExerciseFamilyRecord {
  kind: string;
  familyId: string;
  title: string;
  icon: string;
  domainLabel: string;
  skillId: string;
  minAge: number;
  maxAge: number;
  variantFloor: number;
  speechLocale?: SchoolSpeechLocale;
  deliveryMode?: SchoolExerciseDeliveryMode;
  difficulty?: { min: number; max: number };
  sourceMappings?: readonly { sourceId: string; locator: string; rationale?: string }[];
  prerequisites?: readonly string[];
  items?: readonly SchoolExerciseBankItemRecord[];
}

interface SchoolExerciseRegistryJson {
  schemaVersion: number;
  families: readonly SchoolExerciseFamilyRecord[];
}

interface SchoolExercisePackJson {
  schemaVersion: number;
  packId: string;
  families: readonly SchoolExerciseFamilyRecord[];
}

const baseRegistry = baseRegistryJson as SchoolExerciseRegistryJson;
const crossDomain100 = crossDomain100Json as SchoolExercisePackJson;
const english25 = english25Json as SchoolExercisePackJson;
const englishVisible50 = englishVisible50Json as SchoolExercisePackJson;
const italianVisible50 = italianVisible50Json as SchoolExercisePackJson;
const technologyVisible70 = technologyVisible70Json as SchoolExercisePackJson;
const artVisible70 = artVisible70Json as SchoolExercisePackJson;
const measurement25 = measurement25Json as SchoolExercisePackJson;
const infanziaWorld25 = infanziaWorld25Json as SchoolExercisePackJson;
const infanziaLanguage25 = infanziaLanguage25Json as SchoolExercisePackJson;
const infanziaEnglish25 = infanziaEnglish25Json as SchoolExercisePackJson;
const infanziaBody25 = infanziaBody25Json as SchoolExercisePackJson;
const infanziaArtSensory25 = infanziaArtSensory25Json as SchoolExercisePackJson;
const informaticsVisible50 = informaticsVisible50Json as SchoolExercisePackJson;
const infanziaWorldVisible50 = infanziaWorldVisible50Json as SchoolExercisePackJson;
const historyVisible70 = historyVisible70Json as SchoolExercisePackJson;
const geographyVisible70 = geographyVisible70Json as SchoolExercisePackJson;
const scienceVisible90 = scienceVisible90Json as SchoolExercisePackJson;
const mathVisibleNumbers30 = mathVisibleNumbers30Json as SchoolExercisePackJson;
const mathVisibleCalculation30 = mathVisibleCalculation30Json as SchoolExercisePackJson;
const mathVisibleGeometryMeasure30 = mathVisibleGeometryMeasure30Json as SchoolExercisePackJson;
const musicAudio30 = musicAudio30Json as SchoolExercisePackJson;

const LEGACY_ENGLISH_KIND = 'english-basics-bank';

const TEXT_BANK_SAFE_MIN_AGE = 8;
const applyDeliveryPolicy = (family: SchoolExerciseFamilyRecord): SchoolExerciseFamilyRecord =>
  family.items?.length && family.deliveryMode !== 'visual-audio'
    ? { ...family, minAge: Math.max(TEXT_BANK_SAFE_MIN_AGE, family.minAge) }
    : family;

export const SCHOOL_EXERCISE_FAMILIES: readonly SchoolExerciseFamilyRecord[] = [
  ...baseRegistry.families,
  ...crossDomain100.families
    .filter((family) => family.kind !== LEGACY_ENGLISH_KIND)
    .map(applyDeliveryPolicy),
  ...technologyVisible70.families.map(applyDeliveryPolicy),
  ...english25.families.map(applyDeliveryPolicy),
  ...englishVisible50.families.map(applyDeliveryPolicy),
  ...italianVisible50.families.map(applyDeliveryPolicy),
  ...artVisible70.families.map(applyDeliveryPolicy),
  ...measurement25.families.map(applyDeliveryPolicy),
  ...infanziaWorld25.families.map(applyDeliveryPolicy),
  ...infanziaLanguage25.families.map(applyDeliveryPolicy),
  ...infanziaEnglish25.families.map(applyDeliveryPolicy),
  ...infanziaBody25.families.map(applyDeliveryPolicy),
  ...infanziaArtSensory25.families.map(applyDeliveryPolicy),
  ...informaticsVisible50.families.map(applyDeliveryPolicy),
  ...infanziaWorldVisible50.families.map(applyDeliveryPolicy),
  ...historyVisible70.families.map(applyDeliveryPolicy),
  ...geographyVisible70.families.map(applyDeliveryPolicy),
  ...scienceVisible90.families.map(applyDeliveryPolicy),
  ...mathVisibleNumbers30.families.map(applyDeliveryPolicy),
  ...mathVisibleCalculation30.families.map(applyDeliveryPolicy),
  ...mathVisibleGeometryMeasure30.families.map(applyDeliveryPolicy),
  ...musicAudio30.families.map(applyDeliveryPolicy)
];